import React, { useCallback, useEffect, useMemo, useRef } from "react";
import ReactResizeDetector from "react-resize-detector";
import uuid from "react-uuid";
import _ from "lodash";
import styles from "./MultiSingleHorizontalBar.module.scss";
import { Tooltip } from "../Tooltip";
import { EMPTY_ARRAY } from "../misc";

export const WIDTH_WATCHING_CLASS_NAME = "width-watching";
export const WIDTH_LABEL_WATCHING_CLASS_NAME = "label-width-watching";
export const WIDTH_LABEL_UPDATE_CLASS_NAME = "label-width-update";

export const WIDTH_UPDATE_REQUIRE_CLASS_NAME = "width-update-require";
export const HEIGHT_WATCHING_CLASS_NAME = "height-measure";
export const HEIGHT_UPDATE_REQUIRE_CLASS_NAME = "height-update-require";

const MINIMUM_MARGIN = 16;
const MINIMUM_HEIGHT = 45;

export interface MultiSingleHorizontalBarProps {
    id: string;
    className?: string;
    data: {
        backgroundColors?: string[];
        borderColor?: string;
        data: number[];
        label: string;
    };
    getTooltipJSX?: (index: number) => JSX.Element;
    firstColumnLabels: (JSX.Element | string)[];
    secondColumnLabels: (JSX.Element | string)[];
    queryHeightWatchingElements?: () => {
        watching: HTMLElement[];
        update: HTMLElement[];
    };
    queryLabelWidthWatchingElements?: () => {
        watching: HTMLElement[];
        update: HTMLElement[];
    };
    enableLabelWidthWatching?: boolean;
}
export const MultiSingleHorizontalBar: React.FC<MultiSingleHorizontalBarProps> = ({
    id,
    enableLabelWidthWatching = false,
    className,
    data,
    firstColumnLabels,
    secondColumnLabels,
    getTooltipJSX,
    queryHeightWatchingElements,
    queryLabelWidthWatchingElements
}) => {
    const ref = useRef();
    const isCancelled = useRef(undefined);
    const shouldRender = useRef(false);
    const scrollFunctionIdRef = useRef<string>(uuid());

    useEffect(() => {
        isCancelled.current = false;
        return () => {
            isCancelled.current = true;
        };
    }, []);

    const numbers = data?.data;

    const updateHeights = useCallback(
        (ref: React.MutableRefObject<HTMLElement>) => {
            // let children = Array.from((ref.current as Element).children);
            let children = Array.from(
                (ref.current as Element).querySelectorAll(`.${HEIGHT_UPDATE_REQUIRE_CLASS_NAME}`)
            );

            const heightMeasureChildren = (ref.current as Element).querySelectorAll(`.${HEIGHT_WATCHING_CLASS_NAME}`);
            const heights = _.flattenDeep(
                Array.from(heightMeasureChildren ?? EMPTY_ARRAY).map(d => {
                    return (d as HTMLElement)?.clientHeight ?? 0;
                })
            );

            let extraHeights = [];
            if (queryHeightWatchingElements) {
                const { watching = [], update = [] } = queryHeightWatchingElements();

                extraHeights = _.flattenDeep(
                    Array.from(watching ?? EMPTY_ARRAY).map(d => {
                        return (d as HTMLElement)?.clientHeight ?? 0;
                    })
                );

                children = [...children, ...update];

                // console.log([extra, extraHeights]);
            }

            const maxHeight = _.max([...heights, ...extraHeights]) + 1;

            const margin =
                maxHeight <= MINIMUM_HEIGHT
                    ? MINIMUM_MARGIN
                    : MINIMUM_MARGIN > (maxHeight - MINIMUM_HEIGHT) / 2
                    ? MINIMUM_MARGIN - (maxHeight - MINIMUM_HEIGHT) / 2
                    : 10;

            children.forEach(child => {
                const prev = (child as HTMLElement)?.previousElementSibling?.classList?.contains(
                    HEIGHT_UPDATE_REQUIRE_CLASS_NAME
                );
                const next = (child as HTMLElement)?.nextElementSibling?.classList?.contains(
                    HEIGHT_UPDATE_REQUIRE_CLASS_NAME
                );

                // console.log(maxHeight)
                const _marginTop = prev === true ? margin : 0;
                const _marginBottom = next === true ? margin : 0;

                (child as HTMLElement).style.height = `${maxHeight}px`;
                (child as HTMLElement).style.flexBasis = `${maxHeight}px`;
                (child as HTMLElement).style.marginBottom = `${_marginBottom}px`;
                (child as HTMLElement).style.marginTop = `${_marginTop}px`;
            });

            // for (let i = 0; i < children.length; i++) {
            // find max gap
            // const maxHeight = Math.max(
            //     ...children.map((child, index) => {
            //         const referenceChild = children[index + 1] ?? children[index - 1];
            //         const refHeight = Math.max(
            //             ...Array.from(referenceChild.querySelectorAll(`.${HEIGHT_WATCHING_CLASS_NAME}`)).map(d => {
            //                 return d?.getBoundingClientRect()?.height ?? 0;
            //             })
            //         );

            //         const baseHeight =
            //             Math.max(
            //                 ...Array.from(child.querySelectorAll(`.${HEIGHT_WATCHING_CLASS_NAME}`)).map(d => {
            //                     return d?.getBoundingClientRect()?.height ?? 0;
            //                 })
            //             ) ?? 0;

            //         return refHeight / 2 + baseHeight / 2;
            //     })
            // );

            // children.forEach((child, index) => {
            //     const referenceChild = children[index + 1] ?? children[index - 1];
            //     if (referenceChild) {
            //         const refHeight = Math.max(
            //             ...Array.from(referenceChild.querySelectorAll(`.${HEIGHT_WATCHING_CLASS_NAME}`)).map(d => {
            //                 return d?.getBoundingClientRect()?.height ?? 0;
            //             })
            //         );

            //         const height = 2 * (maxHeight - refHeight / 2);

            //         console.log([id, maxHeight, refHeight]);
            //         (child as HTMLElement).style.height = `${height}px`;
            //         (child as HTMLElement).style.flexBasis = `${height}px`;
            //     }
            //     // (child as HTMLElement).style.height = `${maxHeight}px`;
            //     // (child as HTMLElement).style.flexBasis = `${maxHeight}px`;
            // });
            //}
        },
        [queryHeightWatchingElements]
    );

    // update mid label flex basis
    const updateWidths = useCallback((ref: React.MutableRefObject<HTMLElement>) => {
        if (ref.current) {
            const widthWatchingElements = (ref.current as HTMLElement).querySelectorAll(
                `.${WIDTH_WATCHING_CLASS_NAME}`
            );
            const widthUpdateRequireElements = (ref.current as HTMLElement).querySelectorAll(
                `.${WIDTH_UPDATE_REQUIRE_CLASS_NAME}`
            );

            const maxWidth = Math.max(
                ...Array.from(widthWatchingElements).map(d => {
                    return d.clientWidth ?? 0;
                })
            );

            Array.from(widthUpdateRequireElements).forEach(d => {
                const element = d as HTMLElement;
                element.style.flexBasis = `${maxWidth}px`;
                element.style.maxWidth = `${maxWidth}px`;
            });
        }
    }, []);

    const updateLabelWidths = useCallback(
        (ref: React.MutableRefObject<HTMLElement>) => {
            if (!enableLabelWidthWatching) return;

            if (ref.current) {
                const widthWatchingElements = Array.from(
                    (ref.current as HTMLElement).querySelectorAll(`.${WIDTH_LABEL_WATCHING_CLASS_NAME}`)
                );

                const widthUpdateRequireElements = Array.from(
                    (ref.current as HTMLElement).querySelectorAll(`.${WIDTH_LABEL_UPDATE_CLASS_NAME}`)
                );

                const { watching = [], update = [] } = queryLabelWidthWatchingElements
                    ? queryLabelWidthWatchingElements()
                    : {
                          watching: [],
                          update: []
                      };

                const maxWidth =
                    _.ceil(
                        Math.max(
                            ...Array.from(widthWatchingElements).map(d => {
                                return d.clientWidth ?? 0;
                            }),
                            ...Array.from(watching).map(d => {
                                return d.clientWidth ?? 0;
                            })
                        )
                    ) + 1;

                Array.from([...widthUpdateRequireElements, ...update]).forEach(d => {
                    const element = d as HTMLElement;
                    element.style.minWidth = `${maxWidth}px`;
                });
            }
        },
        [enableLabelWidthWatching, queryLabelWidthWatchingElements]
    );

    const onScroll = useMemo(() => {
        shouldRender.current = true;

        // use this id to avoid race condition running onScroll
        const uid = uuid();
        scrollFunctionIdRef.current = uid;

        // console.log("numbers might be changed", numbers, id);

        return () => {
            // if (shouldRender.current === true) {
            //     console.log("numbers when shouldRender is true", numbers, id);
            // }

            if (ref.current && shouldRender.current && uid === scrollFunctionIdRef.current) {
                // const scrollY = window.scrollY ?? 0;
                // const panelY = (ref.current as Element)?.getBoundingClientRect()?.y ?? 0;
                // const height = (ref.current as Element)?.getBoundingClientRect()?.height ?? 0;
                // const scrollToY = Math.max(scrollY + panelY + height / 2, 0);
                // const scrollToScreen = scrollY + window.innerHeight ?? 0;

                // if (scrollToScreen <= scrollToY) return;

                const scrollY = window.scrollY ?? 0;
                const panelY = (ref.current as Element)?.getBoundingClientRect()?.y ?? 0;
                const height = (ref.current as Element)?.getBoundingClientRect()?.height ?? 0;

                const objectY0 = Math.max(scrollY + panelY, 0);
                const objectY1 = objectY0 + Math.max(window.innerHeight * 0.3, height);
                const screenY0 = scrollY;
                const screenY1 = scrollY + window.innerHeight ?? 0;

                if (!((objectY0 <= screenY1 && screenY0 <= objectY0) || (objectY1 <= screenY1 && screenY0 <= objectY1)))
                    return;

                // adjust bar width
                updateLabelWidths(ref);
                updateWidths(ref);
                const bars = (ref.current as Element).getElementsByClassName("bar");
                const barWrapperElements = (ref.current as HTMLElement).querySelectorAll(`.bar-wrapper`);
                const barMaxWidth = Math.max(
                    ...Array.from(barWrapperElements).map(d => {
                        return d.clientWidth ?? 0;
                    })
                );

                const annotations = (ref.current as Element).getElementsByClassName("annotation");

                for (let i = 0; i < bars.length; i++) {
                    const piece = (numbers ?? [])?.[i] ?? 0;

                    const f = piece >= 100 ? 100 : piece < 0 ? 0 : piece;
                    (bars[i] as HTMLElement).style.width = `${f}%`;

                    (annotations[i] as HTMLElement).style.left = `${(f / 100) * barMaxWidth}px`;
                }

                updateHeights(ref);

                shouldRender.current = false;
            }
        };
    }, [ref, numbers, updateHeights, updateLabelWidths, updateWidths]);

    useEffect(() => {
        // set start detect render
        onScroll();
        const newOnScroll = _.debounce(onScroll, 10);
        // const newOnScroll = onScroll;
        window.addEventListener("scroll", newOnScroll, {
            passive: true
        });
        return () => {
            window.removeEventListener("scroll", newOnScroll);
        };
    }, [onScroll]);

    // useEffect(() => {
    //     updateWidth();
    // }, [updateWidth, numbers]);

    // useEffect(() => {
    //     updateWidths(ref);
    //     updateHeights(ref);
    // });

    useEffect(() => {
        updateWidths(ref);
    });

    const onResizeGraph = useCallback(() => {
        shouldRender.current = true;
        onScroll();
    }, [onScroll]);

    return (
        <div ref={ref} className={`multi-single-horizontal-bar ${className ?? ""}`}>
            {(numbers ?? []).map((___, index) => {
                return (
                    <div className={`${styles.singleLine} single-line ${HEIGHT_UPDATE_REQUIRE_CLASS_NAME}`} key={index}>
                        <div className={`${styles.first} first ${WIDTH_LABEL_UPDATE_CLASS_NAME}`}>
                            {firstColumnLabels?.[index]}
                            <div className={`${styles.hiddenFirst} ${WIDTH_LABEL_WATCHING_CLASS_NAME}`}>
                                {firstColumnLabels?.[index]}
                            </div>
                        </div>

                        <div className={`${styles.last}`}>
                            <div className={`${styles.barContainer} bar-wrapper`}>
                                <Tooltip
                                    id={`${id}-tooltip`}
                                    getTooltipJSX={
                                        getTooltipJSX
                                            ? () => {
                                                  return getTooltipJSX(index);
                                              }
                                            : undefined
                                    }
                                    className={`${styles.bar} bar ${HEIGHT_WATCHING_CLASS_NAME}`}
                                    tooltipStyle={{
                                        maxWidth: "200px"
                                    }}
                                    style={{
                                        width: 0,
                                        background: data?.backgroundColors?.[index] ?? "black"
                                    }}
                                ></Tooltip>
                            </div>
                            <div className={`${WIDTH_UPDATE_REQUIRE_CLASS_NAME}`}></div>

                            <div
                                className={`${WIDTH_WATCHING_CLASS_NAME} ${HEIGHT_WATCHING_CLASS_NAME} ${styles.annotation} annotation`}
                            >
                                {secondColumnLabels?.[index]}
                            </div>
                        </div>
                    </div>
                );
            })}

            <ReactResizeDetector handleWidth={true} handleHeight={false} onResize={onResizeGraph}></ReactResizeDetector>
        </div>
    );
};
